import { useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { insertProjectSchema, type InsertProject } from "@shared/schema";
import { MultiSelectToggle } from "@/components/ui/multi-select-toggle";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileProjectCreate } from "./mobile/mobile-project-create";

const projectTypes = [
  { value: "betoninstandsetzung", label: "Betoninstandsetzung" },
  { value: "oberflachenbehandlung", label: "Oberflächenbehandlung" },
  { value: "rissabdichtung", label: "Rissabdichtung" },
  { value: "korrosionsschutz", label: "Korrosionsschutz" },
  { value: "strukturverstaerkung", label: "Strukturverstärkung" },
  { value: "schutzschichten", label: "Schutzschichten auftragen" },
  { value: "wasserschutz", label: "Wasserschutz" },
  { value: "brandschutz", label: "Brandschutz" },
];

const objektbeschaffenheitOptions = [
  "Verputztes Mauerwerk",
  "Beton",
  "Sichtbeton",
  "Sockelbereich",
  "Balkonuntersicht",
  "Fassade",
  "Plattenfassade",
  "Decke",
  "Unterzug",
  "Attika",
  "Stütze",
  "Brüstung",
  "Fußboden",
];

const massnahmenOptions = [
  "Betoninstandsetzung",
  "Risssanierung",
  "Beschichtung",
  "Spachtelung",
  "Reprofilierung",
  "Korrosionsschutz",
  "Sockelsanierung",
  "Putzsanierung",
  "Anstrich",
  "Abdichtung",
  "Fassadenreinigung",
  "Hochdruckreinigung",
  "Graffitientfernung",
  "Imprägnierung",
  "Plattenfassaden-Sanierung",
  "Plattentausch",
  "Fugensanierung",
  "Unterkonstruktion-Sanierung",
  "Fußbodenreinigung",
  "Fußbodenbeschichtung",
  "Estrichsanierung",
  "Bodenversiegelung",
  "Bodenschleifen",
  "Rutschhemmung",
];

const mengeneinheitOptions = ["m²", "lfm", "Stück", "Pauschale"];

export default function ProjectCreatePage() {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <MobileProjectCreate />;
  }
  return <DesktopProjectCreate />;
}

function DesktopProjectCreate() {
  const [, navigate] = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<InsertProject>({
    resolver: zodResolver(insertProjectSchema),
    defaultValues: {
      name: "",
      type: "",
      customer: "",
      address: "",
      description: "",
      status: "draft",
      currentStep: 1,
      objektbeschaffenheit: [],
      massnahmen: [],
      geschosszahl: undefined,
      hoehe: undefined,
      menge: undefined,
      mengeneinheit: "m²",
    },
  });

  const createProjectMutation = useMutation({
    mutationFn: async (data: InsertProject) => {
      const response = await apiRequest("POST", "/api/projects", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      toast({
        title: "Projekt erfolgreich erstellt",
        description: "Das Projekt wurde erfolgreich gespeichert.",
      });
      navigate("/dashboard");
    },
    onError: () => {
      toast({
        title: "Fehler beim Erstellen",
        description: "Das Projekt konnte nicht erstellt werden.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: InsertProject) => {
    await createProjectMutation.mutateAsync({
      ...data,
      currentStep,
      status: currentStep === totalSteps ? "active" : "draft",
    });
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const saveDraft = () => {
    form.handleSubmit(onSubmit)();
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
            <h1 className="text-4xl font-bold text-bauwerk-teal mb-2">
              Neue Erfassung
            </h1>
            <p className="text-gray-600">Schritt-für-Schritt Bauwerkserfassung</p>
        </div>
        <div className="flex space-x-2">
            {currentStep !== 1 && (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(1)}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Zu Schritt 1
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zum Dashboard
            </Button>
        </div>
      </div>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-bauwerk-teal">
              Schritt {currentStep} von {totalSteps}
            </span>
            <span className="text-sm text-gray-600">
              {currentStep === 1 && "Grunddaten"}
              {currentStep === 2 && "Technische Daten"}
              {currentStep === 3 && "Dokumentation"}
              {currentStep === 4 && "Abschluss"}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-bauwerk-teal">
              {currentStep === 1 && "Projekt-Grunddaten"}
              {currentStep === 2 && "Technische Daten"}
              {currentStep === 3 && "Dokumentation"}
              {currentStep === 4 && "Abschluss"}
            </CardTitle>
            <p className="text-gray-600">
              {currentStep === 1 && "Erfassen Sie die grundlegenden Informationen zu Ihrem Bauwerk"}
              {currentStep === 2 && "Technische Spezifikationen und Baudetails"}
              {currentStep === 3 && "Fotos, Pläne und zusätzliche Dokumente"}
              {currentStep === 4 && "Überprüfung und Projektabschluss"}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Schritt 1: Grunddaten */}
              {currentStep === 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Projektname *</Label>
                    <Input
                      id="name"
                      placeholder="z.B. Wohnhaus Musterstraße 1"
                      {...form.register("name")}
                      className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="type">Tätigkeit *</Label>
                    <Select
                      value={form.watch("type")}
                      onValueChange={(value) => form.setValue("type", value)}
                    >
                      <SelectTrigger className="focus:ring-bauwerk-teal focus:border-bauwerk-teal">
                        <SelectValue placeholder="Tätigkeit auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.type && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.type.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="customer">Auftraggeber</Label>
                    <Input
                      id="customer"
                      placeholder="Name des Auftraggebers"
                      {...form.register("customer")}
                      className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Standort *</Label>
                    <Input
                      id="address"
                      placeholder="Adresse des Bauwerks"
                      {...form.register("address")}
                      className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                    />
                    {form.formState.errors.address && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.address.message}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="description">Projektbeschreibung</Label>
                    <Textarea
                      id="description"
                      rows={4}
                      placeholder="Kurze Beschreibung des Projekts..."
                      {...form.register("description")}
                      className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                    />
                  </div>
                </div>
              )}

              {/* Schritt 2: Technische Daten */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-bauwerk-teal">Technische Spezifikationen</h3>
                  <div className="space-y-2">
                    <Label>Objektbeschaffenheit <span className="text-xs text-gray-500 font-normal">(Mehrfachauswahl möglich)</span></Label>
                    <MultiSelectToggle
                      options={objektbeschaffenheitOptions}
                      selected={form.watch("objektbeschaffenheit") ?? []}
                      onChange={(vals) => form.setValue("objektbeschaffenheit", vals)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Maßnahmen <span className="text-xs text-gray-500 font-normal">(Mehrfachauswahl möglich)</span></Label>
                    <MultiSelectToggle
                      options={massnahmenOptions}
                      selected={form.watch("massnahmen") ?? []}
                      onChange={(vals) => form.setValue("massnahmen", vals)}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Geschosszahl</Label>
                      <Input
                        type="number"
                        min={0}
                        placeholder="z.B. 3"
                        {...form.register("geschosszahl", { valueAsNumber: true })}
                        className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Höhe in m</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.1"
                        placeholder="z.B. 9.5"
                        {...form.register("hoehe", { valueAsNumber: true })}
                        className="focus:ring-bauwerk-teal focus:border-bauwerk-teal"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>Menge / Ausmaß</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="z.B. 150"
                          {...form.register("menge", { valueAsNumber: true })}
                          className="focus:ring-bauwerk-teal focus:border-bauwerk-teal flex-1"
                        />
                        <Select
                          value={form.watch("mengeneinheit") ?? "m²"}
                          onValueChange={(val) => form.setValue("mengeneinheit", val)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {mengeneinheitOptions.map((unit) => (
                              <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Schritt 3: Dokumentation */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <p className="text-gray-600">Fotos und Dokumente können hier hochgeladen werden.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        📷
                      </div>
                      <p className="text-gray-600">Fotos hochladen</p>
                    </div>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        📄
                      </div>
                      <p className="text-gray-600">Dokumente hochladen</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Schritt 4: Abschluss */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="font-semibold text-green-800 mb-2">Projekt bereit zur Erstellung!</h4>
                    <p className="text-green-700">
                      Alle Angaben wurden erfasst. Klicken Sie auf "Projekt erstellen", um die Erfassung abzuschließen.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={previousStep}
                  disabled={currentStep === 1}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <div className="space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={saveDraft}
                    disabled={createProjectMutation.isPending}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Entwurf speichern
                  </Button>
                  {currentStep < totalSteps ? (
                    <Button
                      type="button"
                      onClick={nextStep}
                      className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
                    >
                      Weiter
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={createProjectMutation.isPending}
                      className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
                    >
                      {createProjectMutation.isPending ? "Speichern..." : "Projekt erstellen"}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="opacity-50">
            <CardContent className="p-6">
              <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center mb-4">
                <span className="text-white">📐</span>
              </div>
              <h4 className="font-semibold text-gray-700 mb-2">
                Schritt 2: Technische Daten
              </h4>
              <p className="text-sm text-gray-500">
                Maße, Materialien, Konstruktionsdetails
              </p>
            </CardContent>
          </Card>
          <Card className="opacity-50">
            <CardContent className="p-6">
              <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center mb-4">
                <span className="text-white">📷</span>
              </div>
              <h4 className="font-semibold text-gray-700 mb-2">
                Schritt 3: Dokumentation
              </h4>
              <p className="text-sm text-gray-500">
                Fotos, Pläne, zusätzliche Dokumente
              </p>
            </CardContent>
          </Card>
          <Card className="opacity-50">
            <CardContent className="p-6">
              <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center mb-4">
                <span className="text-white">✓</span>
              </div>
              <h4 className="font-semibold text-gray-700 mb-2">
                Schritt 4: Abschluss
              </h4>
              <p className="text-sm text-gray-500">
                Prüfung und Freigabe der Erfassung
              </p>
            </CardContent>
          </Card>
        </div>
    </div>
  );
}
